export const en = {
  meta: {
    /** Browser-tab <title> — brand + tagline. */
    title: "BackDoor — Hack Smarter, Not Harder!",
    /** Corner wordmark on the auth screens (rendered uppercase via CSS). */
    brand: "BackDoor",
  },

  common: {
    loading: "Loading…",
    cancel: "Cancel",
    confirm: "Confirm",
    close: "Close",
    delete: "Delete",
    save: "Save",
    create: "Create",
    edit: "Edit",
    retry: "Retry",
    refresh: "Refresh",
    copy: "Copy",
    copied: "Copied",
    download: "Download",
    open: "Open",
    yes: "Yes",
    no: "No",
    enter: "Enter",
    dash: "—",
  },

  nav: {
    overview: "Overview",
    findings: "Findings",
    hypoteses: "Hypoteses",
    attackGraph: "Attack graph",
    liveFeed: "Live feed",
    reports: "Reports",
    settings: "Settings",
    signOut: "Sign out",
    menu: "Menu",
    confirmSignOutTitle: "Sign out?",
    confirmSignOutBody: "You'll be returned to the login screen.",
  },

  topbar: {
    llms: "LLMs",
    connection: "connection",
    refresh: "Refresh",
    notifications: "Notifications",
    notifEmpty: "You're all caught up",
    markAllRead: "Mark all as read",
  },

  projects: {
    label: "PROJECTS",
    newProject: "New project",
    newScan: "+ New scan",
    openScans: "Show scans",
    moreActions: "More actions",
    deleteProject: "Delete project",
    noScans: "No scans yet",
    scanRunningTitle: "Scan already running",
    scanRunningBody:
      "A scan is already running for this project. Cancel or finish it before starting a new one.",
    gotIt: "Got it",
    scanStartFailed: "Failed to start scan",
    confirmDeleteTitle: "Delete this project?",
    confirmDeleteBody: 'Project "{name}" and its scope will be permanently removed.',
    deleteFailed: "Failed to delete project",
    statusRunning: "running",
    statusPaused: "paused",
    statusReady: "ready",
    statusCancelled: "cancelled",
    statusFailed: "failed",
  },

  scan: {
    elapsed: "elapsed",
    pause: "Pause",
    resume: "Resume",
    pauseScan: "Pause scan",
    resumeScan: "Resume scan",
    cancelScan: "Cancel scan",
    confirmCancelTitle: "Cancel this scan?",
    confirmCancelBody:
      "Cancelling will stop execution and move the scan to the CANCELLED state.",
    confirmCancelLabel: "Cancel scan",
    none: "No scan running",
    startHint: "Start a new scan from one of your projects.",
    startCta: "Start scan",
    startCtaBusy: "Starting…",
    startCtaTitle: "Press play! Start the scan",
    phaseRecon: "RECON",
    phaseMap: "MAP",
    phaseAnalyze: "ANALYZE",
    phaseExploit: "EXPLOIT",
    phaseChain: "CHAIN",
    phaseReport: "REPORT",
    graphTitle: "Mission graph",
    graphLoading: "Loading graph…",
    graphEmpty: "Select a scan to view the live mission graph.",
    graphNoNodes: "No nodes yet.",
    graphLoadFailed: "Failed to load graph: {error}",
    graphDirTB: "Top → bottom",
    graphDirLR: "Left → right",
    noVerifiedDomain:
      "No verified domains in this project. Verify a domain to start a scan.",
    errTierBlocked:
      "Your tier doesn't include this scan type. Upgrade to start full scans.",
    errBilling: "Scan blocked by billing limits. Check your subscription.",
    errNoPlan: "Pick a plan in Settings to start a scan.",
    errEmailNotVerified:
      "You need to verify your email before starting a scan. We've sent you a confirmation link — please check your inbox.",
    resendEmailLink: "Resend verification email",
    resendEmailSent: "Email sent. Check your inbox, including the Spam folder.",
    resendEmailFailed: "Couldn't send the email. Please try again in a minute.",
    resendEmailCooldown:
      "An email was already sent. Please wait {seconds}s and try again.",
    warmupBanner: "Please wait. For the first 20 minutes we can't show any data",
    stageRecon: "RECON",
    stageScanner: "SCANNER",
    stageRbc: "RBC",
    stageExploit: "EXPLOIT",
  },

  overview: {
    sectionLabel: "OVERVIEW",
    recentFindings: "Recent findings",
    liveFeed: "Live feed",
    metricFindings: "Findings",
    metricHypoteses: "Hypoteses",
    metricEndpoints: "Endpoints",
    metricFindingsSub: "{critical} critical, {high} high",
    metricHypotesesSub: "{reactive} reactive, {chain} chain",
    metricEndpointsSub: "{percent}% crawled",
    viewAllFindings: "View all findings",
    viewFullFeed: "View full live feed",
    warmingUp: "warming up",
    collectingData: "Collecting data…",
    waitingForEvents: "Waiting for first events…",
    pleaseWait20: "Please wait. For the first 20 minutes we can't show any data.",
  },

  feed: {
    sectionLabel: "LIVE FEED",
    loading: "Loading events…",
    loadingMore: "Loading more…",
    empty: "No events yet.",
    end: "End of feed.",
    counter: "{events} events",
  },

  findings: {
    sectionLabel: "FINDINGS ({count})",
    tabAll: "All",
    tabHigh: "High",
    tabMedium: "Medium",
    tabLow: "Low",
    loading: "Loading findings…",
    emptyFiltered: "No findings match this filter yet.",
    emptyNoScan: "No active scan — start a scan from Overview to see findings here.",
    sevCritical: "critical",
    sevHigh: "high",
    sevMedium: "medium",
    sevLow: "low",
    statusConfirmed: "confirmed",
    statusOpen: "open",
    statusRejected: "rejected",
    strictNotice:
      "The report composition was validated by a human: only the findings they confirmed are shown ({confirmed}). Excluded: {excluded}.",
    truncatedNotice: "Showing the first {shown} of {total} findings.",
    pocLabel: "PoC",
    description: "Description",
    cvss: "CVSS",
    score: "Score",
    target: "Target",
    vulnType: "Vuln type",
    tool: "Tool",
    evidence: "Evidence",
    recommendation: "Recommendation",
    url: "URL",
    evidenceLabel: "EVIDENCE",
    untitled: "Untitled finding",
    notAvailable: "N/A",
    loadFailed: "Failed to load findings",
  },

  hypoteses: {
    sectionLabel: "HYPOTESES",
    tabAll: "All",
    tabAnalyze: "Analyze",
    tabExploit: "Exploit",
    tabFinding: "Finding",
    tabRejected: "Rejected",
    loading: "Loading hypoteses…",
    emptyFiltered: "No hypoteses match this filter yet.",
    emptyNoScan: "No active scan — start one to populate hypoteses.",
    badgeAnalyse: "analyse",
    badgeExploit: "exploit",
    badgeFinding: "finding",
    badgeRejected: "rejected",
    viewPoc: "View PoC",
    viewDetails: "Details",
    findingKindDefault: "HYPOTHESIS",
    fieldReason: "Reasoning",
    fieldHowToTest: "How to test",
    fieldTarget: "Target",
    statusReady: "ready",
    statusRejectedShort: "rejected",
    statusAnalyse: "analyse",
    loadFailed: "Failed to load hypoteses",
  },

  reports: {
    sectionLabel: "REPORTS",
    generate: "Generate report",
    generating: "Generating…",
    loading: "Loading reports…",
    empty: "No reports yet. Generate one from the current scan.",
    needScan: "Start a scan before generating a report.",
    statusReady: "ready",
    statusPending: "pending",
    notReady: "The report is still being compiled — wait for the scan to finish.",
    pendingBadge: "Pending validation",
    pendingTitle: "The report was compiled automatically and has not been validated by a human yet",
    verifiedBadge: "Human validated",
    verifiedTitle: "The report composition was manually validated by a human and is current",
    staleBadge: "Validation outdated",
    staleTitle:
      "The report composition changed after the manual validation — it needs to be re-validated",
    revokedBadge: "Validation revoked",
    revokedTitle: "The manual report validation was revoked by an administrator",
    tipPending: "No manual human validation yet.",
    tipVerified: "Report composition validated by {name}, {date}.",
    tipStale: "Validated by {name}, {date}. The composition changed since — needs re-validation.",
    tipRevoked: "Validation from {date} ({name}) was revoked by an administrator.",
    tipCurrentCounts:
      "Now: {total} findings, {confirmed} confirmed, {unreviewed} undecided, {falsePositive} false positives.",
    tipSnapshotCounts:
      "Included {included} of {total}; excluded {excludedUnreviewed} unreviewed and {excludedFalsePositive} false positives.",
    download: "Download",
    read: "Read",
    delete: "Delete",
    deleteAria: "Delete report",
    confirmDeleteTitle: "Delete this report?",
    confirmDeleteBody: "The PDF and its metadata will be permanently removed.",
    confirmDeleteLabel: "Delete report",
    untitled: "Untitled report",
    downloadFailed: "Failed to download report",
    readFailed: "Failed to load report",
    deleteFailed: "Failed to delete report",
    generateFailed: "Failed to generate report",
    previewTitle: "Report preview",
    downloadTitle: "Download PDF",
    deleteBody: "The report and all of its exported artifacts will be removed.",
    loadFailed: "Failed to load report: {message}",
    error: "error",
    scanStatus: "Scan status",
    notFinished: "Scan not finished — report becomes available when the scan terminates.",
    finishedAt: "Finished {date}",
    formatPdf: "PDF",
    formatHtml: "HTML",
    formatJson: "JSON",
    formatSarif: "SARIF",
  },

  graph: {
    sectionLabel: "ATTACK GRAPH",
  },

  chat: {
    open: "Open chat",
    close: "Close chat",
    title: "Chat",
    collapsedLabel: "Chat",
    placeholder: "Message",
    send: "Send",
    copy: "Copy",
    empty: "Steering directives appear here.",
    noScan: "No active scan is selected.",
    sendFailed: "Failed to send steering directive.",
    steeringUnavailable: "Steering is not available yet — the public API doesn't expose this endpoint.",
    // Agent reasoning feed (mission_loop iterations)
    adminOnly: "Agent reasoning is available to administrators only.",
    loadFailed: "Couldn't load agent activity.",
    emptyFeed: "The agent hasn't started reasoning yet.",
    composerHint: "Steering coming soon — read-only for now",
    planning: "Planning {n} attack vectors",
    structuredStep: "Working through a step…",
    findingCritical: "CRITICAL FINDING",
    findingHigh: "Serious finding",
    findingMedium: "Finding",
    findingLow: "Minor issue",
    findingInfo: "Note",
    exploit: "Launching exploit",
    step: "Step complete",
    milestone: "Scan event",
    narrativeThinking: "Agent is thinking…",
    narrativeFallbackTitle: "{kind} · iteration {seq}",
    missionScore: "score {current}/{target}",
    missionStatusPending: "pending",
    missionStatusRunning: "running",
    missionStatusCompleted: "completed",
    missionStatusFailed: "failed",
    missionStatusCancelled: "cancelled",
    missionStatusSkipped: "skipped",
    kindPlan: "Plan",
    kindReplan: "Replan",
    kindScore: "Score",
    kindOther: "Step",
    iterLabel: "Iteration {seq}",
    toolCallsCount: "{count} tool calls",
    scoreDelta: "{before} → {after}",
    reasoningLabel: "Reasoning",
    toolsLabel: "Tool calls",
    noReasoning: "No reasoning recorded for this step.",
    detailFailed: "Couldn't load step detail.",
    exitLabel: "exit {code}",
  },

  onboarding: {
    titleFirst: "Start your first search",
    titleVerify: "Verify your site to start scanning",
    subtitleVerify:
      "Add a TXT record to your DNS, then come back to verify.",
    siteAddress: "Site address",
    siteAddressPlaceholder: "yourdomain.com",
    siteRequired: "Please enter a site address",
    siteInvalid: "This doesn't look like a domain. Example: example.com",
    projectRegistered: "Project registered",
    registerSite: "Register site",
    registering: "Registering site…",
    checking: "Checking DNS record…",
    verified: "Verified — opening dashboard…",
    checkAgain: "Check again",
    registerFailed: "Failed to start verification",
    createFailed: "Failed to create project",
    sessionExpired:
      "This verification session has expired on the server. Add the site again to get a fresh TXT token.",
    verificationFailed: "Verification failed",
    verificationRestarted:
      "The token expired — we issued a new one. Update your DNS TXT record and click \"Re-check\".",
    dnsType: "Type",
    dnsName: "Name",
    dnsValue: "Data",
    dnsInstructions:
      "Create a TXT record on your DNS with these values, then click Check again.",
    subtitleVerifyExisting: "Finish DNS verification for one of your projects, or add a new site.",
    step1Title: "Enter your site address. Site should be yours",
    step1Placeholder: "enter your site here",
    projectRegisteredTitle: "Project registered",
    step2Title: "Add a TXT record to your DNS",
    whereIsIt: "Where is it?",
    whereIsItUrl: "https://www.google.com/search?q=how+to+add+dns+txt+record",
    domainVerified: "Domain verified. Opening dashboard…",
    iAddedRecord: "I added the TXT record — check now",
    ttlMin: "{minutes} min",
    ttlHour: "1 hour",
    dnsCantFind:
      "We couldn't find the TXT record yet. DNS can take a few minutes to propagate — try again shortly.",
    confirmDeleteTitle: "Delete this project?",
    confirmDeleteBody: 'Project "{name}" and its scope will be permanently removed.',
    confirmDeleteLabel: "Delete project",
    deleteFailed: "Failed to delete project",

    // First-run onboarding (tour + MCP)
    chooseTitle: "Welcome aboard",
    chooseSubtitle: "How would you like to get started?",
    tourCardTitle: "Take a quick tour",
    tourCardBody: "A guided walkthrough of the interface — run a scan, read findings, get your report.",
    mcpCardTitle: "Connect an AI agent",
    mcpCardBody: "Plug our MCP server into your own agent and let it drive BackDoor for you.",
    skip: "Skip for now",
    back: "Back",
    next: "Next",
    stepOf: "{n} of {total}",
    tourFinish: "Start scanning",
    tour: {
      "scan-cta": {
        title: "Start a scan here",
        body: "Point BackDoor at your verified site and it kicks off the whole attack pipeline.",
      },
      metrics: {
        title: "Your results at a glance",
        body: "Findings, hypotheses and discovered endpoints land in these cards as the scan runs.",
      },
      "scan-stages": {
        title: "Live progress",
        body: "Recon, mapping, analysis, exploitation, chaining and reporting — watch each stage advance.",
      },
      "reports-nav": {
        title: "Your report",
        body: "When the scan finishes, the full write-up with working exploits shows up under Reports.",
      },
    },
    mcpTitle: "Connect an AI agent over MCP",
    mcpSubtitle: "Add this to your agent's MCP config, then ask it to run and explain a scan for you.",
    mcpStep1: "Copy the config below.",
    mcpStep2: "Paste it into your agent's MCP settings and replace the token with yours.",
    mcpStep3: "Ask your agent: “Run a BackDoor scan and walk me through it.”",
    mcpDone: "Choose a plan",
  },

  auth: {
    welcomeTitle: "Hi! Welcome",
    back: "Back",
    emailPlaceholder: "Email",
    login: "Login",
    signUp: "Sign up",
    invalidEmail: "Enter a valid email",
    ssoFailed: "Failed to start SSO",
    continueWith: "Continue with {provider}",
    passwordTitle: "Password",
    passwordPlaceholder: "Enter your password",
    passwordRequired: "Enter your password",
    signingIn: "Signing in…",
    enter: "Enter",
    loginFailed: "Login failed",
    errInvalidCredentials: "Invalid email or password",
    errEmailTaken: "This email is already registered",
    registrationTitle: "Registration",
    createPassword: "Create password",
    name: "Name",
    surname: "Surname",
    organisation: "Organisation",
    organisationSize: "Organisation size",
    industry: "Industry",
    passwordWeak: "Password must be at least 8 characters",
    repeatPassword: "Repeat password",
    passwordsMatch: "Passwords match",
    passwordsMismatch: "Passwords do not match",
    showPassword: "Show password",
    hidePassword: "Hide password",
    generatePassword: "Generate strong password",
    generatingPassword: "Generating…",
    passwordCopied: "Strong password generated and copied to your clipboard",
    passwordGenerated: "Strong password generated — save it somewhere safe",
    strengthTooShort: "Too short",
    strengthWeak: "Weak",
    strengthOkay: "Okay",
    strengthGood: "Good",
    strengthStrong: "Strong",
    creating: "Creating account…",
    signupFailed: "Signup failed",
    verificationTitle: "Verification",
    verificationCodePlaceholder: "Enter verification code",
    verificationCodeRequired: "Enter the verification code from your email",
    verificationCodeShort: "The verification code must be at least 8 characters",
    verificationFailed: "Verification failed. Please try again.",
    verificationInvalid:
      "This verification code is invalid, expired, or already used. Request a new one.",
    verificationTooMany: "Too many attempts. Please wait a moment and try again.",
    sendCodeFailed: "Failed to send verification email",
    checking: "Checking…",
    resendCode: "Resend code",
    codeSentTo: "Verification code was sent to {email}.",
    canResendIn: "You can send one more in {seconds} sec",
    freshCodeSent: "A fresh verification email has been sent.",
    maskedFallback: "your email",
    forgotPassword: "Forgot your password?",
    forgotTitle: "Reset password",
    forgotIntro:
      "Enter the email for your account and we'll send a reset link.",
    forgotSubmit: "Send reset link",
    forgotSending: "Sending…",
    forgotSent: "Check {email} for the reset link.",
    forgotResend: "Resend link",
    forgotResendIn: "You can resend in {seconds} sec",
    forgotFailed: "Couldn't send reset link",
    backToLogin: "Back to sign in",
    resetTitle: "Set a new password",
    resetIntro: "Enter a new password to finish recovery.",
    resetSubmit: "Save and sign in",
    resetSaving: "Saving…",
    resetFailed: "Couldn't reset your password",
    resetTokenMissing: "This reset link is invalid or incomplete.",
    resetTokenInvalid:
      "This reset link is invalid, expired, or already used. Request a new one.",
    changePasswordTitle: "Change password",
    currentPasswordPlaceholder: "Current password",
    newPasswordPlaceholder: "New password",
    changePasswordSubmit: "Change password",
    changePasswordSaving: "Updating…",
    changePasswordSuccess: "Password updated.",
    changePasswordFailed: "Couldn't change password",
    changePasswordInvalid: "Current password is incorrect.",
    currentPasswordRequired: "Enter your current password",
    industryTechnology: "Technology",
    industryFinance: "Finance",
    industryHealthcare: "Healthcare",
    industryManufacturing: "Manufacturing",
    industryRetail: "Retail",
    industryGovernment: "Government",
    industryEducation: "Education",
    industryEnergy: "Energy",
    industryMedia: "Media",
    industryOther: "Other",
    callbackSigningIn: "Signing you in…",
    callbackRedirecting: "Returning to login…",
    callbackErrorUNAUTHENTICATED: "Authentication failed. Please try again.",
    callbackErrorPERMISSION_DENIED: "Access denied for this provider.",
    callbackErrorNOT_FOUND: "Authentication request not found or expired.",
    callbackErrorINVALID_ARGUMENT: "Invalid authentication request.",
    callbackErrorINTERNAL: "Authentication service error. Please try again later.",
    callbackErrorUnknown: "Authentication failed.",
    callbackErrorMissingTokens: "The login response was incomplete. Please try again.",
  },

  newProject: {
    title: "New project",
    namePlaceholder: "Project name",
    hostPlaceholder: "yourdomain.com",
    invalidHost: "Enter a valid host",
    createFailed: "Failed to create project",
    createDomainFailed: "Project created, but failed to add domain",
    createBusy: "Creating…",
  },

  // Jurisdiction names themselves arrive from the API in English and are shown
  // as-is in both languages — only these surrounding UI strings are localized.
  jurisdiction: {
    placeholder: "Report jurisdiction (optional)",
    caption:
      "Sets the report’s “Legal & regulatory risks” section. It is independent of the interface and report language, and applies to your next report.",
    dialogTitle: "Project jurisdiction",
    menuItem: "Jurisdiction…",
    saveFailed: "Failed to update jurisdiction",
  },

  settings: {
    sectionLabel: "SETTINGS",
    tourHeading: "Product tour",
    tourBody: "Take a guided demo on sample data — add a site, run a scan, read a finding and get the report.",
    tourMobile: "The product tour is available on a computer.",
    tourCta: "Take the tour",
    payments: "Payments",
    /** Sidebar badge + Payments card heading: names the plan, not a quota. */
    currentPlanLabel: "Current plan",
    /** Sidebar badge value when the user has no subscription yet. */
    noPlanShort: "No plan",
    /** Sidebar badge caption above the subscription's end date. */
    subscriptionUntil: "Subscription until",
    paymentsScansSub: "Subscription info will appear once loaded.",
    /** Payments CTA — buys a first plan, or switches the current one. */
    pickPlanCta: "Choose plan",
    changePlanCta: "Change plan",
    paymentsProcessing: "Processing…",
    checkoutFailed: "Failed to start checkout",
    checkoutStarted: "Checkout started — redirecting…",
    /** Shown on return from the payment provider. */
    checkoutSuccess: "Payment received — your plan is up to date.",
    /** Paid, but the provider's webhook hasn't reached us yet. */
    checkoutProcessing:
      "Payment is being processed — refresh the page in a minute to see the update.",
    checkoutCancelled: "Payment cancelled — nothing was charged.",
    checkoutMissing: "Checkout URL is missing",
    payNow: "Pay {amount}",
    checkoutCurrencyUnavailable: "This plan isn't available in your currency yet.",
    checkoutEmailNotVerified:
      "Please verify your email first — we've sent you a link. Checkout will be available once it's confirmed.",
    scanPackTitle: "Extra scans",
    scanPackPerScan: "{price} / scan",
    scanPackQuantity: "Quantity",
    scanPackDecrement: "Decrease quantity",
    scanPackIncrement: "Increase quantity",
    scanPackBuy: "Buy {count} for {total}",
    scanPackUnavailable: "Extra scans aren't available right now.",
    scanPackLoadFailed: "Couldn't load extra-scan pricing",
    profile: "Profile",
    email: "Email",
    password: "Password",
    displayName: "Display name",
    profileEditDisabled: "Profile editing requires a user profile update endpoint.",
    language: "Language",
    saveChanges: "Save changes",
    profileInfo: "Profile info",
    transactionHistoryLabel: "TRANSACTION HISTORY",
    paymentMethodLabel: "PAYMENT METHOD",
    invoicesEmpty: "No transactions yet",
    invoicesLoadFailed: "Couldn't load transactions",
    downloadInvoice: "Download invoice",
    downloadUnavailable: "Available after payment",
    invoiceLabel: "Subscription",
    invoiceStatusPaid: "Paid",
    invoiceStatusPending: "Awaiting payment",
    invoiceStatusExpired: "Overdue",
    invoiceStatusFailed: "Payment failed",
    invoiceStatusRefunded: "Refunded",
    invoiceStatusIssued: "Issued",
    noPlanTitle: "No active plan",
    noPlanBody:
      "Pick a plan to start scanning. You can change or cancel any time.",
    choosePlanHeading: "Choose plan",
    currentPlanBadge: "Current",
    choosePlan: "Choose",
    choosingPlan: "Redirecting…",
    scansPerMonth: "{count} scans / month",
    scansUnlimited: "Unlimited scans",
    plansLoadFailed: "Couldn't load plans",
    /** Stripe adds tax on top of catalog prices — say so before checkout. */
    taxNote: "Prices exclude tax — the final amount is shown at checkout.",
  },

  paywall: {
    title: "Subscription — Unlimited Scans",
    subtitle:
      "Pricing is shown per asset. The second and each additional asset costs $5,000/month.",
    perAsset: "month per asset",
    /** Savings badge; amount is computed from the plan catalogue. */
    save: "Save {amount}",
    mostPopular: "Most Popular",
    bestValue: "Best Value — Get {months} Months Free",
    m1Title: "1 Month",
    m1Billed: "{amount} billed monthly",
    m1Tagline: "Try it with no commitment. Cancel anytime.",
    m1FeatUnlimited: "Unlimited scans for your asset",
    m1FeatDepth: "Standard scan depth",
    m1FeatRetests: "Unlimited retests after fixes",
    m1FeatStart: "Scan starts within 12 hours",
    m1FeatReports: "Reports with working exploits + dashboard",
    m1FeatIntegrations: "Slack, Telegram, webhooks",
    m1FeatSupport: "Support within 8 business hours",
    m1Cta: "Start with 1 Month",
    m3Title: "3 Month",
    m3Billed: "{amount} per quarter",
    m3Tagline: "Twice the compute power per target and automated patching.",
    m3Lead: "Everything in the 1 Month plan, plus:",
    m3FeatDepth: "Extended scan depth — 2× more compute power per target",
    m3FeatQueue: "Priority queue — scan starts within 4 hours",
    m3FeatAutoPatch: "Auto Patch — ready-to-merge fix PR",
    m3FeatChains: "Attack chains across your assets",
    m3FeatSupport: "Dedicated support (≤4 hours response time)",
    m3FeatWhiteLabel: "White-label reports",
    m3FeatJira: "Jira & CI/CD integration",
    m3FeatContract: "Contract + NDA instead of a standard proposal",
    m3Cta: "Choose 3 Months",
    m12Title: "12 Months",
    m12Billed: "{amount} per year",
    m12Tagline: "Continuous mode: the agent never stops.",
    m12Lead: "Everything in the 3 Months plan, plus:",
    m12FeatDepth: "Maximum scan depth — 4× more compute power per target",
    m12FeatContinuous: "Continuous mode with no schedule or limits",
    m12FeatVip: "VIP queue — scan starts within 1 hour, 24/7",
    m12FeatRemediation: "Automatic remediation based on your rules",
    m12FeatPaths: "Attack path modeling to critical assets",
    m12FeatTam: "Dedicated Technical Account Manager + private Slack Connect",
    m12FeatCompliance: "Compliance package: SOC 2, ISO 27001, PCI DSS",
    m12FeatBoardReport: "Quarterly board-level security report",
    m12FeatIntegrations: "ServiceNow, SIEM, SSO/SAML integrations",
    m12FeatPriceLock: "Price locked for 24 months",
    m12FeatSla: "Custom SLA with financial guarantees",
    m12Cta: "Choose 12 Months",
  },

  errors: {
    generic: "Something went wrong.",
    network: "Network error. Check your connection.",
    serverUnavailable: "Server temporarily unavailable ({status}). Please try again in a minute.",
    tooManyRequests: "Too many requests — we'll retry automatically in a moment.",
  },

  consent: {
    title: "Cookie consent",
    body: "We use cookies to run this app and, with your consent, to measure usage with Google Analytics.",
    privacy: "Privacy Policy",
    necessaryOnly: "Necessary only",
    allowAll: "Allow all",
  },
  tour: {
    next: "Next",
    back: "Back",
    skip: "Skip",
    stepOf: "{{current}} of {{total}}",
    banner: "Demo mode — sample data, nothing is sent to the engine",
    bannerBack: "Back to the tour",
    bannerExit: "Exit demo",
    bannerShort: "Demo mode",
    stages: {
      site: "Site",
      verify: "Verify",
      scan: "Scan",
      finding: "Finding",
      report: "Report",
    },
    alarm: {
      title: "Critical vulnerability found",
      body: "SQL injection on {host} — customer data is reachable without a login.",
      cta: "Open it",
    },
    welcome: {
      title: "Welcome to BackDoor",
      titlenamed: "Welcome to BackDoor, {name}!",
      body: "This is a demo — add a site, run a scan, read a finding and get the report. It takes about four minutes.",
      start: "Start tour",
    },
    site: {
      title: "Start with your site",
      body: "Everything begins with a domain. We've pre-filled a sample shop — you can leave it or type your own.",
    },
    register: {
      title: "Add the site",
      body: "Click Register site to add it as a project.",
      nudge: "Click Register site to continue.",
    },
    dns: {
      title: "Prove the site is yours",
      body: "In a real scan you add this TXT record to your DNS — that's how we confirm you own the site.",
    },
    verify: {
      title: "Check the record",
      body: "Click to verify. In the demo it passes right away; a real check waits for DNS to propagate.",
      nudge: "Click to verify the record.",
    },
    project: {
      title: "Your project",
      body: "The site is verified. From here you start a scan, open past scans with the arrow, and add another site with the plus.",
    },
    start: {
      title: "Start the scan",
      body: "A real scan runs for up to four hours. Click Start scan.",
      nudge: "Click Start scan to begin.",
    },
    recon: {
      title: "Reconnaissance",
      body: "The engine maps the site: subdomains, ports, stack. Press Next to move the scan forward.",
    },
    map: {
      title: "Live progress",
      body: "Events stream into the feed and the graph grows as the engine works. Next to continue.",
    },
    chat: {
      title: "The assistant",
      body: "Open the assistant — it narrates what the scan is doing in plain language.",
      nudge: "Click the chat rail to open it.",
    },
    chatRead: {
      title: "Plain-language narration",
      body: "The assistant turns the scan's raw events into a readable story. Take a moment to read it, then Next.",
    },
    analyze: {
      title: "What the agent found",
      body: "Three counters: Vulnerabilities are confirmed holes, Hypotheses are what the agent is testing right now, Endpoints is how much of the attack surface it mapped.",
    },
    finding: {
      title: "A critical finding",
      body: "The agent confirmed a SQL injection. Open it to see the details.",
      nudge: "Click the finding to open it.",
    },
    read: {
      title: "The finding",
      body: "Expand it to read what was found, where, and the evidence.",
      nudge: "Click the finding to expand it.",
    },
    fix: {
      title: "How to fix it",
      body: "Every finding comes with remediation — concrete steps to close the issue. Next to continue the scan.",
    },
    more: {
      title: "More findings",
      body: "As the scan finishes, more findings land here. Filter them by severity with these tabs.",
    },
    toGraph: {
      title: "The attack graph",
      body: "Open the Attack graph to see how the engine moved through the site.",
      nudge: "Click Attack graph in the sidebar.",
    },
    graph: {
      title: "Attack graph",
      body: "Each node is a step the engine took. Next to finish the scan.",
    },
    toReport: {
      title: "Your report",
      body: "The scan is done. Open Reports to get the write-up.",
      nudge: "Click Reports in the sidebar.",
    },
    report: {
      title: "Report ready",
      body: "Every scan produces a report, reviewed by a human analyst.",
    },
    readReport: {
      title: "Read it",
      body: "Open the report to preview it.",
      nudge: "Click Read to open the preview.",
    },
    preview: {
      title: "The report",
      body: "Executive summary, every finding with evidence and remediation — ready to share.",
    },
    download: {
      title: "Take it with you",
      body: "Download the report as a file to hand to your team or a client.",
    },
    done: {
      title: "That's the whole cycle",
      body: "Add a site, verify it, scan, read the findings with fixes, get the report. Ready to try it on your own site?",
      cta: "Finish & add my site",
    },
  },
};
